import { useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import {
  selectCurrentObservation,
  selectObservationsStatus, 
  selectObservationsError,
} from '../features/observations/logic/Slice';
import { 
  createObservationsThunk, 
  getObservationsThunk, 
  updateObservationsThunk 
} from '../features/observations/logic/Thunk';
import {
    UpdateObservationsRequest,
    CaseObservations,
} from '../api/Observations'
import { createContent as createContentThunk } from '../thunks/Content';
import { ContentType } from '../api/Content';


type ObservationField = keyof UpdateObservationsRequest;

export interface UseCaseObservationsReturn {
  observation: CaseObservations | null;
  isLoading: boolean;
  error: string | null | undefined;
  onGenerate: () => Promise<string | undefined>;
  onUpdateField: (field: ObservationField, value: string | null) => Promise<void>;
  onImprove: (existingText: string) => Promise<string | null>;
  handleRetry: () => void;
}

export const useCaseObservations = (): UseCaseObservationsReturn => {
  const dispatch = useAppDispatch();
  const { id: caseId } = useParams<{ id: string }>();
  
  const observation = useAppSelector((state) => selectCurrentObservation(state, caseId));
  const status = useAppSelector((state) => selectObservationsStatus(state, caseId));
  const error = useAppSelector((state) => selectObservationsError(state, caseId));
  const isLoading = status === 'loading';

  useEffect(() => {
    if (caseId && !observation && status !== 'loading') {
      dispatch(getObservationsThunk({ case_id: caseId }));
    }
  }, [dispatch, caseId, observation, status]);
  
  const onGenerate = useCallback(async () => {
    if (!caseId) return;

    try {
        const action = await dispatch(createObservationsThunk({ case_id: caseId }));
        if (createObservationsThunk.fulfilled.match(action)) {
            return undefined;
        }
    } catch (err) {
      console.error('Failed to generate observations:', err);
      throw err;
    }
    return undefined;
  }, [caseId, dispatch]);

  const onUpdateField = useCallback(async (field: ObservationField, value: string | null) => {
    if (!caseId || value === null) return;

    const updateRequest: UpdateObservationsRequest = {
      [field]: { 
        value, 
        ai_generated: observation?.[field]?.ai_generated ?? "false", 
        user_updated: "true"
      }
    };

    if (field === 'lapses_identified' && !value) {
        updateRequest.lapse_root_cause = {
            value: '',
            ai_generated: observation?.lapse_root_cause?.ai_generated ?? "false",
            user_updated: "true"
        };
    }

    await dispatch(updateObservationsThunk({ caseId, request: updateRequest }));
  }, [caseId, observation, dispatch]);

  const onImprove = useCallback(async (existingText: string) => {
    try {
      const response = await dispatch(createContentThunk({
        type: ContentType.DEFAULT,
        existing_content: existingText,
        context: {} 
      })).unwrap();
      
      return response.content;
    } catch (err) {
      console.error("Failed to improve content:", err);
      throw err;
    }
  }, [dispatch]);

  const handleRetry = useCallback(() => {
    if (caseId) {
      dispatch(getObservationsThunk({ case_id: caseId }));
    }
  }, [caseId, dispatch]);

  return {
    observation: observation ?? null,
    isLoading,
    error,
    onGenerate,
    onUpdateField,
    onImprove,
    handleRetry,
  };
};